import React, { useState, useEffect, useRef } from 'react';
import { Alert, ProgressBar, Badge, ListGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faCheckCircle, faTimesCircle, faCog } from '@fortawesome/free-solid-svg-icons';

const ModifierProgressDisplay = ({ jobId, onJobComplete, onJobFailed, apiBaseUrl }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [progressMessages, setProgressMessages] = useState([]);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [showDetails, setShowDetails] = useState(false);
    const eventSourceRef = useRef(null);

    useEffect(() => {
        if (jobId) {
            connectToProgressStream();
        }

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [jobId]);

    const connectToProgressStream = () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const url = `${apiBaseUrl}/admin/modifiers/progress/stream/${jobId}`;
        eventSourceRef.current = new EventSource(url);
        
        eventSourceRef.current.onopen = () => {
            setIsConnected(true);
            addProgressMessage({
                type: 'connection_established',
                message: '📡 Connected to progress stream',
                timestamp: new Date().toISOString()
            });
        };
        
        eventSourceRef.current.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                handleProgressUpdate(data);
            } catch (error) {
                console.error('Error parsing progress data:', error);
            }
        };
        
        eventSourceRef.current.onerror = (error) => {
            console.error('SSE Error:', error);
            setIsConnected(false);
            addProgressMessage({
                type: 'error',
                message: '❌ Connection error occurred',
                timestamp: new Date().toISOString()
            });
        };
    };

    const handleProgressUpdate = (data) => {
        addProgressMessage(data);
        setCurrentStatus(data);

        // Update progress percentage
        if (data.progress_percentage !== undefined) {
            setProgressPercentage(data.progress_percentage);
        }

        // Handle job completion
        if (data.type === 'job_complete') {
            setProgressPercentage(100);
            if (onJobComplete) {
                onJobComplete(data);
            }
            setTimeout(() => {
                if (eventSourceRef.current) {
                    eventSourceRef.current.close();
                    setIsConnected(false);
                }
            }, 2000);
        } else if (data.type === 'job_failed') {
            if (onJobFailed) {
                onJobFailed(data);
            }
            setTimeout(() => {
                if (eventSourceRef.current) {
                    eventSourceRef.current.close();
                    setIsConnected(false);
                }
            }, 2000);
        }
    };

    const addProgressMessage = (data) => {
        setProgressMessages(prev => [...prev.slice(-19), {
            ...data,
            id: Date.now() + Math.random(),
            timestamp: data.timestamp || new Date().toISOString()
        }]);
    };

    const getStatusBadge = () => {
        if (!currentStatus) return <Badge bg="secondary">Initializing</Badge>;
        
        switch (currentStatus.type) {
            case 'job_started':
                return <Badge bg="info"><FontAwesomeIcon icon={faPlay} /> Starting</Badge>;
            case 'phase_started':
                return <Badge bg="warning"><FontAwesomeIcon icon={faCog} /> Processing</Badge>;
            case 'phase_completed':
                return <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} /> Phase Complete</Badge>;
            case 'job_complete':
                return <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} /> Completed</Badge>;
            case 'job_failed':
                return <Badge bg="danger"><FontAwesomeIcon icon={faTimesCircle} /> Failed</Badge>;
            default:
                return <Badge bg="info"><FontAwesomeIcon icon={faCog} /> Running</Badge>;
        }
    };

    const getProgressVariant = () => {
        if (!currentStatus) return 'info';
        
        switch (currentStatus.type) {
            case 'job_complete':
                return 'success';
            case 'job_failed':
                return 'danger';
            case 'phase_started':
            case 'batch_started':
                return 'info';
            default:
                return 'primary';
        }
    };

    const getCurrentMessage = () => {
        return currentStatus?.message || 'Waiting for updates...';
    };

    if (!jobId) {
        return null;
    }

    return (
        <div className="modifier-progress-display mt-3">
            <Alert variant="light" className="border">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                        <strong>Job Progress</strong> {getStatusBadge()}
                        <span className="text-muted ms-2">Job ID: {jobId}</span>
                    </div>
                    <div>
                        <Badge bg={isConnected ? 'success' : 'secondary'} className="me-2">
                            {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
                        </Badge>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowDetails(!showDetails)}
                        >
                            {showDetails ? 'Hide Details' : 'Show Details'}
                        </Button>
                    </div>
                </div>
                
                <div className="mb-2">
                    <small className="text-muted d-block">{getCurrentMessage()}</small>
                </div>
                
                <ProgressBar 
                    now={progressPercentage} 
                    variant={getProgressVariant()}
                    label={progressPercentage > 0 ? `${progressPercentage}%` : undefined}
                    className="mb-2"
                    height={8}
                />

                {showDetails && (
                    <div className="mt-3">
                        <h6>Progress Details</h6>
                        <div 
                            className="border rounded p-2 bg-light"
                            style={{ maxHeight: '200px', overflowY: 'auto' }}
                        >
                            {progressMessages.length === 0 ? (
                                <small className="text-muted">No progress messages yet...</small>
                            ) : (
                                <ListGroup variant="flush">
                                    {progressMessages.slice(-10).map((msg) => (
                                        <ListGroup.Item 
                                            key={msg.id} 
                                            className="py-1 px-2 border-0 bg-transparent"
                                        >
                                            <div className="d-flex justify-content-between">
                                                <small className="text-dark">{msg.message}</small>
                                                <small className="text-muted">
                                                    {new Date(msg.timestamp).toLocaleTimeString()}
                                                </small>
                                            </div>
                                            {msg.anumber && (
                                                <small className="text-muted d-block">A-number: {msg.anumber}</small>
                                            )}
                                            {msg.records_processed && (
                                                <small className="text-muted d-block">Records: {msg.records_processed}</small>
                                            )}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </div>
                    </div>
                )}
            </Alert>
        </div>
    );
};

export default ModifierProgressDisplay;